import * as Sentry from "@sentry/browser";

type GuardedNodeMethods = {
  insertBefore: typeof Node.prototype.insertBefore;
  removeChild: typeof Node.prototype.removeChild;
  replaceChild: typeof Node.prototype.replaceChild;
};

type GuardedMethod = "insertBefore" | "removeChild" | "replaceChild";

let guardedNodeMethods: GuardedNodeMethods | null = null;
const reportedMethods = new Set<string>();

// One report per method per session is enough to spot a rising trend without flooding Sentry
const reportGuardHit = (method: GuardedMethod): void => {
  if (reportedMethods.has(method)) return;
  reportedMethods.add(method);
  // Deferred so a DOM commit is never interrupted by the reporting itself
  setTimeout(() => {
    Sentry.captureMessage(`Reconciler DOM guard absorbed a stale ${method} anchor`, {
      level: "warning",
      tags: { reconcilerDomGuard: method },
    });
  }, 0);
};

// Tools that rewrite text nodes in the live DOM — Crowdin in-context, browser page translation,
// writing-assistant extensions — leave React reconciling against a node they already detached, and
// the commit throws NotFoundError. These guards degrade that to a misplaced node instead of a crash.
// Every correct call takes the native path untouched.
export const installReconcilerDomGuard = (): void => {
  if (guardedNodeMethods) return;

  const original: GuardedNodeMethods = {
    insertBefore: Node.prototype.insertBefore,
    removeChild: Node.prototype.removeChild,
    replaceChild: Node.prototype.replaceChild,
  };
  guardedNodeMethods = original;

  Node.prototype.insertBefore = function guardedInsertBefore<T extends Node>(this: Node, node: T, child: Node | null): T {
    if (child && child.parentNode !== this) {
      reportGuardHit("insertBefore");
      return original.insertBefore.call(this, node, null) as T;
    }
    return original.insertBefore.call(this, node, child) as T;
  };

  Node.prototype.removeChild = function guardedRemoveChild<T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      reportGuardHit("removeChild");
      return child;
    }
    return original.removeChild.call(this, child) as T;
  };

  Node.prototype.replaceChild = function guardedReplaceChild<T extends Node>(this: Node, node: Node, child: T): T {
    if (child.parentNode !== this) {
      reportGuardHit("replaceChild");
      original.insertBefore.call(this, node, null);
      return child;
    }
    return original.replaceChild.call(this, node, child) as T;
  };
};

export const revertReconcilerDomGuard = (): void => {
  if (!guardedNodeMethods) return;
  Node.prototype.insertBefore = guardedNodeMethods.insertBefore;
  Node.prototype.removeChild = guardedNodeMethods.removeChild;
  Node.prototype.replaceChild = guardedNodeMethods.replaceChild;
  guardedNodeMethods = null;
  reportedMethods.clear();
};

export const isReconcilerDomGuardInstalled = (): boolean => guardedNodeMethods !== null;
